<template>
  <div
    :class="[
      $vuetify.theme.dark ? 'responses-theme-dark' : 'responses-theme-light',
      { 'responses-mode-manage': canWrite },
    ]"
  >
    <v-row>
      <v-col cols="12">
        <v-skeleton-loader
          v-if="helpers.dataIsLoading"
          elevation="2"
          type="article"
        ></v-skeleton-loader>
        <v-card v-else>
          <v-card-title>
            <span class="mr-4 subtitle-2 font-weight-medium">Filter</span>
            <v-btn-toggle
              v-model="helpers.timeSlotsFilter"
              dense
              borderless
              mandatory
              class="my-2"
            >
              <v-btn small value="all">Alle Terminoptionen</v-btn>
              <v-btn small value="onlyPossible">Besetzbare</v-btn>
              <v-btn small value="onlyNotPossible">Nicht besetzbare</v-btn>
            </v-btn-toggle>
            <v-btn-toggle
              v-model="helpers.responseFilter"
              dense
              borderless
              mandatory
              class="my-2 ml-2"
            >
              <v-btn small value="allUsers">Alle Benutzer</v-btn>
              <v-btn small value="onlyResponded">Mit Rückmeldung</v-btn>
              <v-btn small value="onlyPositive">Nur positive</v-btn>
            </v-btn-toggle>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="helpers.search"
              append-icon="mdi-magnify"
              hide-details
              dense
              clearable
              outlined
              class="matrix-search"
              label="Mitglieder suchen"
            ></v-text-field>
            <template v-if="canWrite">
              <v-menu
                v-model="helpers.autoAssignment.menuOpen"
                offset-y
                :close-on-content-click="false"
                :disabled="helpers.autoAssignment.loading"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    color="orange"
                    dark
                    depressed
                    class="ml-2"
                    :loading="helpers.autoAssignment.loading"
                    :disabled="helpers.autoAssignment.loading"
                  >
                    <v-icon left>mdi-auto-fix</v-icon>
                    Automatisch einteilen
                  </v-btn>
                </template>

                <v-card min-width="360">
                  <v-card-text class="pb-2">
                    <div class="subtitle-2 mb-2">Einteilungsmodus</div>
                    <v-select
                      v-model="helpers.autoAssignment.selectedMode"
                      :items="assignmentModeItems"
                      item-text="label"
                      item-value="value"
                      dense
                      hide-details
                      outlined
                      class="matrix-assignment-menu-select"
                    ></v-select>
                  </v-card-text>
                  <v-card-actions class="mx-2">
                    <v-spacer></v-spacer>
                    <v-btn
                      text
                      :disabled="helpers.autoAssignment.loading"
                      @click="helpers.autoAssignment.menuOpen = false"
                    >
                      Abbrechen
                    </v-btn>
                    <v-btn
                      color="orange"
                      dark
                      depressed
                      :loading="helpers.autoAssignment.loading"
                      :disabled="helpers.autoAssignment.loading"
                      @click="runAutoAssignment"
                    >
                      Einteilung starten
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-menu>
            </template>

            <v-btn
              v-if="showCreateEventsButton"
              color="primary"
              depressed
              class="ml-2"
              :disabled="createEventsButtonCount === 0"
              @click="$emit('create-events-click')"
            >
              <v-icon left>mdi-calendar-plus</v-icon>
              {{ createEventsButtonCount }} Veranstaltungen erstellen
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-0">
            <div class="responses-matrix-wrapper">
              <div
                class="responses-matrix"
                :style="{ '--timeslot-columns': timeSlotsDataOutput.length }"
              >
                <div class="responses-matrix-row responses-matrix-header">
                  <div
                    class="responses-matrix-cell responses-matrix-member-cell responses-matrix-member-header-cell"
                  ></div>
                  <div
                    v-for="timeSlot in timeSlotsDataOutput"
                    :key="timeSlot.id"
                    class="responses-matrix-cell responses-matrix-timeslot-cell responses-matrix-timeslot-header-cell"
                  >
                    <div class="d-flex align-start justify-space-between">
                      <div
                        class="font-weight-medium text-truncate"
                        :class="
                          $vuetify.theme.isDark ? 'white--text' : 'black--text'
                        "
                      >
                        {{ timeSlot.title }}
                      </div>
                      <div class="d-flex align-center">
                        <v-tooltip bottom v-if="isEventCreated(timeSlot)">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              icon
                              x-small
                              :to="{
                                name: 'scheduling-event-details-tab-general',
                                params: {
                                  organizationId: $route.params.organizationId,
                                  itemId: timeSlot.id,
                                },
                              }"
                            >
                              <v-icon x-small>mdi-open-in-new</v-icon>
                            </v-btn>
                          </template>
                          <span>Zur Veranstaltung</span>
                        </v-tooltip>
                        <v-checkbox
                          v-if="
                            enableEventCreation && !isEventCreated(timeSlot)
                          "
                          class="mt-0 pt-0 mx-0 px-0"
                          :input-value="isTimeSlotSelected(timeSlot.id)"
                          hide-details
                          dense
                          @change="toggleTimeSlotSelection(timeSlot.id, $event)"
                        ></v-checkbox>
                      </div>
                    </div>
                    <div class="caption text--secondary">
                      {{ getReadableTimeSlotDate(timeSlot.start) }}
                    </div>
                    <div class="caption text--secondary">
                      bis {{ getReadableTimeSlotDate(timeSlot.end) }}
                    </div>
                    <v-chip
                      :color="statusColor(timeSlot.resourceStatus.status)"
                      small
                      label
                      :class="[
                        'font-weight-medium text--darken-4 mt-2',
                        statusStyle(timeSlot.resourceStatus.status),
                      ]"
                    >
                      <v-icon left x-small>{{
                        statusIcon(timeSlot.resourceStatus.status)
                      }}</v-icon>
                      {{
                        statusText(
                          timeSlot.resourceStatus.status,
                          timeSlot.responses.confirmations.length +
                            timeSlot.responses.ifNecessary.length,
                          timeSlot.requestedResources.minPersons,
                          timeSlot.requestedResources.maxPersons,
                        )
                      }}
                    </v-chip>
                  </div>
                </div>

                <div
                  v-for="group in groupedRequestResponses"
                  :key="`group-${group.key}`"
                  class="responses-matrix-group-block"
                >
                  <div class="responses-matrix-group-row">
                    <div
                      class="responses-matrix-group-cell responses-matrix-group-label-cell"
                    >
                      {{ group.title }}
                      <span class="caption text--secondary">
                        ({{ group.items.length }})
                      </span>
                    </div>
                    <div class="responses-matrix-group-divider-cell"></div>
                  </div>

                  <div
                    v-for="response in group.items"
                    :key="response.user.uid"
                    class="responses-matrix-row"
                  >
                    <div
                      class="responses-matrix-cell responses-matrix-member-cell"
                    >
                      <v-list-item class="px-0 py-0 my-0">
                        <v-avatar
                          rounded
                          size="36"
                          :color="
                            $vuetify.theme.dark
                              ? 'rgba(255, 255, 255, 0.12)'
                              : 'grey lighten-2'
                          "
                          class="font-weight-medium mr-3"
                          >{{ initials(response.user.displayName) }}</v-avatar
                        >

                        <v-list-item-content class="py-0">
                          <v-list-item-title
                            class="subtitle-2 font-weight-medium"
                            >{{ response.user.displayName }}</v-list-item-title
                          >
                          <v-list-item-subtitle class="caption"
                            >OBM</v-list-item-subtitle
                          >
                        </v-list-item-content>
                      </v-list-item>

                      <v-text-field
                        v-if="canWrite"
                        class="mt-2"
                        :value="response.comment"
                        @input="updateComment(response, $event)"
                        dense
                        hide-details
                        outlined
                        label="Kommentar"
                      ></v-text-field>
                      <div
                        v-else-if="response.comment"
                        class="caption text--secondary mt-2"
                      >
                        <v-icon small class="mr-2">mdi-comment-text</v-icon
                        >{{ response.comment }}
                      </div>
                      <div
                        v-if="
                          saveStateByUid[response.user.uid]?.saving ||
                          saveStateByUid[response.user.uid]?.error
                        "
                        class="caption mt-1"
                      >
                        <span
                          v-if="saveStateByUid[response.user.uid]?.saving"
                          class="orange--text text--darken-2"
                          >Speichern...</span
                        >
                        <span
                          v-else-if="saveStateByUid[response.user.uid]?.error"
                          class="red--text text--darken-2"
                          >{{ saveStateByUid[response.user.uid].error }}</span
                        >
                      </div>
                    </div>

                    <div
                      v-for="timeSlot in timeSlotsDataOutput"
                      :key="`${response.user.uid}-${timeSlot.id}`"
                      class="responses-matrix-cell responses-matrix-timeslot-cell"
                      :class="
                        !canWrite
                          ? [
                              getResponseStatusBackgroundClass(
                                getResponseStatusByTimeSlot(
                                  response,
                                  timeSlot.id,
                                ),
                              ),
                              getResponseStatusTextClass(
                                getResponseStatusByTimeSlot(
                                  response,
                                  timeSlot.id,
                                ),
                              ),
                            ]
                          : []
                      "
                    >
                      <template v-if="canWrite">
                        <div class="manage-timeslot-controls">
                          <v-select
                            class="manage-timeslot-select"
                            :items="statusItems"
                            item-text="label"
                            item-value="value"
                            dense
                            hide-details
                            outlined
                            :value="
                              getResponseStatusByTimeSlot(response, timeSlot.id)
                            "
                            @change="
                              updateStatus(response, timeSlot.id, $event)
                            "
                          >
                            <template v-slot:item="{ item }">
                              <div class="d-flex align-center">
                                <v-icon
                                  small
                                  class="mr-2"
                                  :color="item.iconColor"
                                  >{{ item.icon }}</v-icon
                                >
                                <span>{{ item.label }}</span>
                              </div>
                            </template>
                            <template v-slot:selection="{ item }">
                              <div
                                v-if="item"
                                class="d-flex align-center manage-timeslot-selection"
                              >
                                <v-icon
                                  small
                                  class="mr-2"
                                  :color="item.iconColor"
                                  >{{ item.icon }}</v-icon
                                >
                                <span class="text-truncate">{{
                                  item.label
                                }}</span>
                              </div>
                            </template>
                          </v-select>
                          <v-btn
                            class="manage-accept-btn"
                            text
                            :color="
                              getResponseAcceptedByTimeSlot(
                                response,
                                timeSlot.id,
                              )
                                ? 'orange lighten-4'
                                : ''
                            "
                            @click="toggleAccepted(response, timeSlot.id)"
                          >
                            <v-icon
                              :color="
                                getResponseAcceptedByTimeSlot(
                                  response,
                                  timeSlot.id,
                                )
                                  ? 'orange darken-2'
                                  : ''
                              "
                            >
                              {{
                                getResponseAcceptedByTimeSlot(
                                  response,
                                  timeSlot.id,
                                )
                                  ? "mdi-star"
                                  : "mdi-star-outline"
                              }}
                            </v-icon>
                          </v-btn>
                        </div>
                        <v-text-field
                          class="mt-2"
                          :value="
                            getResponseCommentByTimeSlot(response, timeSlot.id)
                          "
                          @input="
                            updateTimeSlotComment(response, timeSlot.id, $event)
                          "
                          dense
                          hide-details
                          outlined
                          label="Kommentar"
                        ></v-text-field>
                      </template>
                      <template v-else>
                        <div class="d-flex align-center">
                          <v-icon
                            small
                            :class="
                              getResponseStatusTextClass(
                                getResponseStatusByTimeSlot(
                                  response,
                                  timeSlot.id,
                                ),
                              )
                            "
                            >{{
                              statusIconByResponseValue(
                                getResponseStatusByTimeSlot(
                                  response,
                                  timeSlot.id,
                                ),
                              )
                            }}</v-icon
                          >
                          <v-spacer></v-spacer>
                          <v-icon
                            v-if="
                              getResponseAcceptedByTimeSlot(
                                response,
                                timeSlot.id,
                              )
                            "
                            small
                            color="orange darken-2"
                            class="ml-1"
                            >mdi-star</v-icon
                          >
                          <v-icon
                            v-if="
                              getResponseCommentByTimeSlot(
                                response,
                                timeSlot.id,
                              )
                            "
                            small
                            class="ml-1"
                            :class="
                              getResponseStatusTextClass(
                                getResponseStatusByTimeSlot(
                                  response,
                                  timeSlot.id,
                                ),
                              )
                            "
                            >mdi-comment-text</v-icon
                          >
                        </div>
                        <div
                          v-if="
                            getResponseCommentByTimeSlot(response, timeSlot.id)
                          "
                          class="caption text--secondary mt-2"
                        >
                          {{
                            getResponseCommentByTimeSlot(response, timeSlot.id)
                          }}
                        </div>
                        <div
                          v-if="
                            getResponseMetaByTimeSlot(response, timeSlot.id)
                          "
                          class="caption text--secondary mt-1"
                        >
                          <v-icon small class="mr-1">mdi-message-alert</v-icon>
                          Bearbeitet
                          <span
                            v-if="
                              getResponseMetaByTimeSlot(response, timeSlot.id)
                                .lastUpdatedByDisplayName
                            "
                          >
                            von
                            {{
                              getResponseMetaByTimeSlot(response, timeSlot.id)
                                .lastUpdatedByDisplayName
                            }}
                          </span>
                          <span
                            v-if="
                              formatTimestampValue(
                                getResponseMetaByTimeSlot(response, timeSlot.id)
                                  .lastUpdatedAt,
                              )
                            "
                          >
                            am
                            {{
                              formatTimestampValue(
                                getResponseMetaByTimeSlot(response, timeSlot.id)
                                  .lastUpdatedAt,
                              )
                            }}
                            Uhr
                          </span>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>

                <div
                  v-if="filteredRequestResponsesMatched.length === 0"
                  class="pa-8 text-center text--secondary"
                >
                  Keine Rückmeldungen für den aktuellen Filter vorhanden.
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { auth, db } from "@/firebase";
import { ADMIN, AVAILABILITY } from "@/store/modules.js";
import {
  GET_USER_LIST,
  RUN_REQUEST_AUTO_ASSIGNMENT,
  UPDATE_REQUEST_RESPONSE,
} from "@/store/action-types.js";
import { AVAILABILITY_REQUESTS_RESPONSES_write } from "@/data/permission-types.js";

export default {
  name: "request-responses-matrix",
  props: {
    request: {
      type: Object,
      required: true,
    },
    mode: {
      type: String,
      default: "view",
      validator: (value) => ["view", "manage"].includes(value),
    },
    enableEventCreation: {
      type: Boolean,
      default: false,
    },
    showCreateEventsButton: {
      type: Boolean,
      default: false,
    },
    createEventsButtonCount: {
      type: Number,
      default: 0,
    },
    selectedTimeSlotIds: {
      type: Array,
      default: () => [],
    },
    forcedEventCreatedTimeSlotIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      requestResponses: [],
      responseUnsubscribe: null,
      commentSaveTimeoutByUid: {},
      saveStateByUid: {},
      helpers: {
        dataIsLoading: true,
        timeSlotsFilter: "all",
        responseFilter: "allUsers",
        search: "",
        autoAssignment: {
          selectedMode: "prioritizeAtLeastOneAssignmentPerPerson",
          menuOpen: false,
          loading: false,
        },
      },
      assignmentModeItems: [
        {
          value: "prioritizeAtLeastOneAssignmentPerPerson",
          label: "Ausgewogen (mind. eine Einteilung)",
        },
        {
          value: "prioritizeMaxTimeSlots",
          label: "Maximale Besetzung je Terminoption",
        },
        {
          value: "prioritizeFirstComeFirstServe",
          label: "First Come, First Serve",
        },
      ],
      statusItems: [
        {
          value: "0",
          label: "Keine Rückmeldung",
          icon: "mdi-help-circle",
          iconColor: "blue-grey",
        },
        {
          value: "1",
          label: "Verfügbar",
          icon: "mdi-check-circle",
          iconColor: "success",
        },
        {
          value: "3",
          label: "Wenn notwendig",
          icon: "mdi-minus-circle",
          iconColor: "warning",
        },
        {
          value: "2",
          label: "Nicht verfügbar",
          icon: "mdi-close-circle",
          iconColor: "error",
        },
        {
          value: "4",
          label: "Nicht in Stadt",
          icon: "mdi-airplane",
          iconColor: "info",
        },
      ],
    };
  },
  computed: {
    canWritePermission() {
      return this.checkPermission(AVAILABILITY_REQUESTS_RESPONSES_write);
    },
    canWrite() {
      return this.mode === "manage" && this.canWritePermission;
    },
    memberships() {
      return this.$store.state.admin.membershipsPublic || [];
    },
    membershipsPrepared() {
      const rangeStart = this.getDateObject(this.request?.timeSlots?.start);
      const rangeEnd = this.getDateObject(this.request?.timeSlots?.end);
      if (!rangeStart || !rangeEnd) {
        return [];
      }
      const range = {
        start: rangeStart,
        end: rangeEnd,
      };
      const membershipsFiltered = this.memberships.filter((obj) => {
        return (
          new Date(obj.user.memberSinceToDate) <= new Date(range.start) &&
          !(new Date(obj.user.memberUntilToDate) <= new Date(range.end)) &&
          obj.access.type === "member"
        );
      });
      const attendance = this.request?.config?.attendance;
      if (attendance?.isLimited) {
        const allowedAttendeesIds = attendance.allowedAttendeesIds || [];
        return membershipsFiltered
          .filter((obj) => {
            return (
              allowedAttendeesIds.includes(obj.user.uid) ||
              allowedAttendeesIds.includes(obj.group.id)
            );
          })
          .map((item) => {
            return {
              group: {
                title: item.group.title,
                sortKey: item.group.sortKey,
                id: item.group.id,
              },
              user: {
                displayName: item.user.displayName,
                uid: item.user.uid,
              },
            };
          });
      }

      return membershipsFiltered.map((item) => {
        return {
          group: {
            title: item.group.title,
            sortKey: item.group.sortKey,
            id: item.group.id,
          },
          user: {
            displayName: item.user.displayName,
            uid: item.user.uid,
          },
        };
      });
    },
    requestResponsesMatched() {
      const memberships = this.membershipsPrepared;
      const matched = this.requestResponses.map((response) => {
        const responseUid = response?.meta?.uid || "";
        const matchedMembership = memberships.find(
          (membership) => membership.user.uid === responseUid,
        );
        const fallbackUser = {
          displayName:
            this.getDisplayName(responseUid) || responseUid || "Unbekannt",
          uid: responseUid,
        };
        const fallbackGroup = response.group || {
          title: "",
          sortKey: "",
          id: "",
        };

        return {
          ...response,
          ...(matchedMembership || {}),
          user: matchedMembership?.user || response.user || fallbackUser,
          group: matchedMembership?.group || response.group || fallbackGroup,
          status: Array.isArray(response.status) ? response.status : [],
          comment: response.comment || "",
        };
      });

      const missing = memberships.filter((membership) => {
        return matched.every((item) => item.user?.uid !== membership.user.uid);
      });

      const missingResponses = missing.map((membership) => ({
        ...membership,
        comment: "",
        meta: {
          uid: membership.user.uid,
          lastUpdatedBy: "",
        },
        status: [],
      }));

      const output = matched.concat(missingResponses);
      if (this.request?.config?.attendance?.isLimited) {
        const allowedAttendeesIds =
          this.request?.config?.attendance?.allowedAttendeesIds || [];

        return output.filter((obj) => {
          return (
            allowedAttendeesIds.includes(obj.user?.uid) ||
            allowedAttendeesIds.includes(obj.group?.id) ||
            obj.status.length === 0
          );
        });
      }

      return output;
    },
    filteredRequestResponsesMatched() {
      const search = (this.helpers.search || "").toLowerCase().trim();
      const sorted = [...this.requestResponsesMatched].sort((a, b) => {
        const groupSortA = a.group?.sortKey || "";
        const groupSortB = b.group?.sortKey || "";
        if (groupSortA !== groupSortB) {
          return groupSortA > groupSortB ? 1 : -1;
        }
        return (a.user?.displayName || "").localeCompare(
          b.user?.displayName || "",
          "de",
        );
      });

      if (!search) {
        return this.applyResponseFilter(sorted);
      }
      const searchFiltered = sorted.filter((response) => {
        return (
          (response.user?.displayName || "").toLowerCase().includes(search) ||
          (response.group?.title || "").toLowerCase().includes(search)
        );
      });
      return this.applyResponseFilter(searchFiltered);
    },
    groupedRequestResponses() {
      const groupsMap = new Map();

      this.filteredRequestResponsesMatched.forEach((response) => {
        const title = response.group?.title || "Ohne Gruppe";
        const key = response.group?.id || title;

        if (!groupsMap.has(key)) {
          groupsMap.set(key, {
            key,
            title,
            items: [],
          });
        }

        groupsMap.get(key).items.push(response);
      });

      return Array.from(groupsMap.values());
    },
    statusLookupByUser() {
      const lookup = {};

      this.requestResponsesMatched.forEach((response) => {
        const uid = response.user?.uid || response.meta?.uid;
        if (!uid) {
          return;
        }

        const statusBySlot = {};
        (response.status || []).forEach((statusEntry) => {
          statusBySlot[statusEntry.id] = statusEntry;
        });

        lookup[uid] = statusBySlot;
      });

      return lookup;
    },
    requestResponsesMatchedComments() {
      return this.requestResponsesMatched.filter((response) => {
        return (response.comment || "").trim() !== "";
      });
    },
    totalResponsesCount() {
      const responses = this.requestResponsesMatched || [];
      let complete = 0;
      let incomplete = 0;
      let notResponded = 0;

      responses.forEach((response) => {
        const responseStatus = this.getMemberResponseStatus(response);
        if (responseStatus === "complete") {
          complete++;
        } else if (responseStatus === "incomplete") {
          incomplete++;
        } else if (responseStatus === "notResponded") {
          notResponded++;
        }
      });

      return {
        complete,
        incomplete,
        notResponded,
      };
    },
    timeSlotsData() {
      const timeSlots = this.request?.timeSlots?.data || [];
      const responses = this.requestResponsesMatched;
      return timeSlots.map((timeSlot) => {
        const matchingResponses = responses
          .filter((response) => {
            return response.status.some((status) => status.id === timeSlot.id);
          })
          .map((response) => {
            const matchingStatus = response.status.find((status) => {
              return status.id === timeSlot.id;
            });
            return {
              ...response,
              status: matchingStatus,
            };
          });

        return {
          ...timeSlot,
          responses: {
            all: matchingResponses,
            confirmations: matchingResponses.filter(
              (response) => response.status.status === "1",
            ),
            cancellations: matchingResponses.filter(
              (response) => response.status.status === "2",
            ),
            ifNecessary: matchingResponses.filter(
              (response) => response.status.status === "3",
            ),
            away: matchingResponses.filter(
              (response) => response.status.status === "4",
            ),
            awaitingResponse: matchingResponses.filter(
              (response) => response.status.status === "0",
            ),
          },
          resourceStatus: this.getTimeslotStatus(
            timeSlot.requestedResources,
            matchingResponses,
          ),
        };
      });
    },
    timeSlotsDataOutput() {
      if (this.helpers.timeSlotsFilter === "onlyPossible") {
        return this.timeSlotsData.filter(
          (timeSlot) =>
            timeSlot.resourceStatus.status === "target" ||
            timeSlot.resourceStatus.status === "overhang",
        );
      }
      if (this.helpers.timeSlotsFilter === "onlyNotPossible") {
        return this.timeSlotsData.filter(
          (timeSlot) => timeSlot.resourceStatus.status === "deficit",
        );
      }
      return this.timeSlotsData;
    },
    matrixStats() {
      return {
        totalResponsesCount: this.totalResponsesCount,
        possibleTimeSlots:
          this.countTimeSlotsByStatus("target") +
          this.countTimeSlotsByStatus("overhang"),
        notPossibleTimeSlots: this.countTimeSlotsByStatus("deficit"),
      };
    },
  },
  created() {
    this.initialize();
  },
  beforeDestroy() {
    this.cleanupSnapshot();
    this.clearCommentTimers();
  },
  watch: {
    $route() {
      this.initialize();
    },
    request: {
      handler(newValue) {
        if (newValue) {
          this.syncAutoAssignmentModeWithRequest();
          this.getMemberships();
          this.initialize();
        }
      },
      immediate: true,
      deep: false,
    },
    matrixStats: {
      handler(newStats) {
        this.$emit("stats-change", newStats);
      },
      immediate: true,
      deep: true,
    },
    requestResponsesMatchedComments: {
      handler(newComments) {
        this.$emit("comments-change", newComments || []);
      },
      immediate: true,
      deep: true,
    },
    timeSlotsData: {
      handler(newTimeSlots) {
        this.$emit("time-slots-change", newTimeSlots || []);
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    async initialize() {
      if (
        !this.request ||
        !this.$route.params.organizationId ||
        !this.$route.params.itemId
      ) {
        return;
      }

      this.helpers.dataIsLoading = true;
      await this.getMemberships();
      this.subscribeResponses();
    },
    subscribeResponses() {
      this.cleanupSnapshot();
      this.requestResponses = [];

      this.responseUnsubscribe = db
        .collection("organizations")
        .doc(this.$route.params.organizationId)
        .collection("requests")
        .doc(this.$route.params.itemId)
        .collection("requestResponses")
        .onSnapshot(
          (querySnapshot) => {
            const data = [];
            querySnapshot.forEach((doc) => {
              data.push({
                ...doc.data(),
              });
            });
            this.requestResponses = data;
            this.helpers.dataIsLoading = false;
          },
          (error) => {
            console.log("Error getting request responses:", error);
            this.helpers.dataIsLoading = false;
          },
        );
    },
    cleanupSnapshot() {
      if (this.responseUnsubscribe) {
        this.responseUnsubscribe();
        this.responseUnsubscribe = null;
      }
    },
    clearCommentTimers() {
      Object.values(this.commentSaveTimeoutByUid).forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      this.commentSaveTimeoutByUid = {};
    },
    syncAutoAssignmentModeWithRequest() {
      const requestDefaultMode =
        this.request?.config?.defaultAutoAssignmentMode;
      const validModes = this.assignmentModeItems.map((item) => item.value);
      this.helpers.autoAssignment.selectedMode = validModes.includes(
        requestDefaultMode,
      )
        ? requestDefaultMode
        : "prioritizeAtLeastOneAssignmentPerPerson";
    },
    async runAutoAssignment() {
      if (!this.canWrite) {
        return;
      }

      this.helpers.autoAssignment.loading = true;
      let isSuccessful = true;
      try {
        await this.$store.dispatch(
          `${AVAILABILITY}${RUN_REQUEST_AUTO_ASSIGNMENT}`,
          {
            organizationId: this.$route.params.organizationId,
            requestId: this.$route.params.itemId,
            createEvents: false,
            mode: this.helpers.autoAssignment.selectedMode,
          },
        );
      } catch (error) {
        isSuccessful = false;
        console.error("Error in runAutoAssignment:", error);
      } finally {
        this.helpers.autoAssignment.loading = false;
      }

      if (isSuccessful) {
        this.helpers.autoAssignment.menuOpen = false;
      }
    },
    getTimeslotStatus(requestedResources = {}, responses) {
      const responseCount =
        this.getStatusCount("1", responses) +
        this.getStatusCount("3", responses);

      const minCheck = requestedResources.minPersons
        ? responseCount >= requestedResources.minPersons
        : responseCount >= 1;

      const maxCheck = requestedResources.maxPersons
        ? responseCount <= requestedResources.maxPersons
        : true;

      const statusResult =
        minCheck && maxCheck ? "target" : !minCheck ? "deficit" : "overhang";

      return {
        status: statusResult,
        minCheck,
        maxCheck,
        unitCheck: true,
      };
    },
    statusText(status, count, min, max) {
      if (!min) {
        min = 0;
      }
      let additionalInfoCount;
      switch (status) {
        case "deficit":
          additionalInfoCount = count - min;
          return additionalInfoCount === 0
            ? "Nicht besetzbar"
            : `Nicht besetzbar (${additionalInfoCount})`;
        case "target":
          return "Besetzbar";
        case "overhang":
          additionalInfoCount = count - max;
          return additionalInfoCount === 0
            ? "Besetzbar (Überhang)"
            : `Besetzbar (Überhang) (${additionalInfoCount})`;
        default:
          return "";
      }
    },
    statusColor(status) {
      switch (status) {
        case "deficit":
          return "red lighten-4";
        case "target":
          return "green lighten-4";
        case "overhang":
          return "orange lighten-4";
        default:
          return "";
      }
    },
    statusIcon(status) {
      switch (status) {
        case "deficit":
          return "mdi-arrow-down-circle";
        case "target":
          return "mdi-check-circle";
        case "overhang":
          return "mdi-arrow-up-circle";
        default:
          return "";
      }
    },
    statusStyle(status) {
      switch (status) {
        case "deficit":
          return "red--text";
        case "target":
          return "green--text";
        case "overhang":
          return "orange--text";
        default:
          return "";
      }
    },
    checkPermission(item) {
      if (item === "" || item === undefined) {
        return true;
      }
      return this.$store.getters["organization/checkPermissionByID"](item);
    },
    getStatusCount(statusId, responses) {
      if (statusId === "all") {
        return (
          this.countStatusById("1", responses) +
          this.countStatusById("2", responses) +
          this.countStatusById("3", responses) +
          this.countStatusById("4", responses)
        );
      }
      return this.countStatusById(statusId, responses);
    },
    countStatusById(statusId, responses) {
      return responses.reduce((n, response) => {
        return n + (response.status.status === statusId);
      }, 0);
    },
    countTimeSlotsByStatus(status) {
      return this.timeSlotsData.reduce((n, response) => {
        return n + (response.resourceStatus.status === status);
      }, 0);
    },
    checkifUserIsInvited(uid, userGroupId, attendance) {
      if (attendance.isLimited) {
        return (
          attendance.allowedAttendeesIds.includes(uid) ||
          attendance.allowedAttendeesIds.includes(userGroupId) ||
          attendance.allowedAttendeesIds.includes("all")
        );
      }
      return true;
    },
    getMemberResponseStatus(response) {
      const timeSlots = this.request?.timeSlots?.data || [];
      const responseIsComplete = timeSlots.every((timeSlot) => {
        return response.status.find((status) => status.id === timeSlot.id);
      });

      if (responseIsComplete) {
        return "complete";
      }
      if (
        response.status.length > 0 &&
        timeSlots.length !== response.status.length
      ) {
        return "incomplete";
      }
      if (
        !this.checkifUserIsInvited(
          response.meta?.uid || response.user?.uid,
          response.group?.id,
          this.request.config.attendance,
        )
      ) {
        return "not invited";
      }
      return "notResponded";
    },
    async getMemberships() {
      if (!this.request) {
        return;
      }
      if (this.request.status === "planned") {
        await this.$store.dispatch(`${ADMIN}${GET_USER_LIST}`, {
          organizationId: this.$route.params.organizationId,
          queryIsFiltered: true,
          query: {
            type: "active",
          },
        });
      } else {
        await this.$store.dispatch(`${ADMIN}${GET_USER_LIST}`, {
          organizationId: this.$route.params.organizationId,
          queryIsFiltered: false,
          query: {
            type: "archived",
            memberUntil: this.convertToDate(
              this.request.timeSlots.start.timestamp,
            ),
          },
        });
      }
    },
    getDisplayName(uid) {
      const user = this.memberships.find(
        (membership) => membership.user.uid === uid,
      );
      return user?.user?.displayName || "";
    },
    convertToDate(date) {
      date = date.toDate();
      return (
        (date.getDate() < 10 ? "0" : "") +
        date.getDate() +
        "-" +
        (date.getMonth() + 1 < 10 ? "0" : "") +
        (date.getMonth() + 1) +
        "-" +
        date.getFullYear()
      );
    },
    initials(fullName) {
      if (!fullName) {
        return "?";
      }
      const namesArray = fullName.trim().split(" ");
      if (namesArray.length === 1) {
        return `${namesArray[0].charAt(0)}`;
      }
      return `${namesArray[0].charAt(0)}${namesArray[
        namesArray.length - 1
      ].charAt(0)}`;
    },
    getResponseStatusByTimeSlot(response, timeSlotId) {
      const statusItem = this.getStatusItemByTimeSlot(response, timeSlotId);
      return statusItem?.status || "0";
    },
    getResponseCommentByTimeSlot(response, timeSlotId) {
      const statusItem = this.getStatusItemByTimeSlot(response, timeSlotId);
      return statusItem?.comment || "";
    },
    getResponseAcceptedByTimeSlot(response, timeSlotId) {
      const statusItem = this.getStatusItemByTimeSlot(response, timeSlotId);
      return !!statusItem?.isAccepted;
    },
    getResponseMetaByTimeSlot(response, timeSlotId) {
      const statusItem = this.getStatusItemByTimeSlot(response, timeSlotId);
      return statusItem?.meta || null;
    },
    getStatusItemByTimeSlot(response, timeSlotId) {
      const uid = response.user?.uid || response.meta?.uid;
      if (!uid) {
        return null;
      }

      return this.statusLookupByUser[uid]?.[timeSlotId] || null;
    },
    formatTimestampValue(timestampLike) {
      if (!timestampLike) {
        return "";
      }
      let dateValue = null;
      if (timestampLike?.toDate) {
        dateValue = timestampLike.toDate();
      } else if (timestampLike instanceof Date) {
        dateValue = timestampLike;
      } else {
        dateValue = new Date(timestampLike);
      }
      if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
        return "";
      }
      const options = {
        weekday: "short",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      };
      return dateValue.toLocaleDateString("de-DE", options);
    },
    getStatusItemMeta() {
      return {
        lastUpdatedAt: new Date().toISOString(),
        lastUpdatedBy: auth.currentUser?.uid || "",
        lastUpdatedByDisplayName: auth.currentUser?.displayName || "",
      };
    },
    isEventCreated(timeSlot) {
      if (timeSlot?.eventCreated) {
        return true;
      }
      return this.forcedEventCreatedTimeSlotIds.includes(timeSlot?.id);
    },
    isTimeSlotSelected(timeSlotId) {
      return this.selectedTimeSlotIds.includes(timeSlotId);
    },
    toggleTimeSlotSelection(timeSlotId, value) {
      const nextSelection = [...this.selectedTimeSlotIds];
      const index = nextSelection.indexOf(timeSlotId);
      const shouldSelect = value === true;

      if (shouldSelect && index < 0) {
        nextSelection.push(timeSlotId);
      }
      if (!shouldSelect && index >= 0) {
        nextSelection.splice(index, 1);
      }

      this.$emit("selected-time-slot-ids-change", nextSelection);
    },
    statusLabelByResponseValue(statusValue) {
      const item = this.statusItems.find(
        (status) => status.value === statusValue,
      );
      return item?.label || "Keine Rückmeldung";
    },
    statusIconByResponseValue(statusValue) {
      const item = this.statusItems.find(
        (status) => status.value === statusValue,
      );
      return item?.icon || "mdi-help-circle";
    },
    statusChipColor(statusValue) {
      switch (statusValue) {
        case "1":
          return "green lighten-4";
        case "2":
          return "red lighten-4";
        case "3":
          return "orange lighten-4";
        case "4":
          return "blue lighten-4";
        default:
          return "brown lighten-4";
      }
    },
    getResponseStatusBackgroundClass(statusValue) {
      switch (statusValue) {
        case "1":
          return "response-bg-success";
        case "2":
          return "response-bg-error";
        case "3":
          return "response-bg-warning";
        case "4":
          return "response-bg-info";
        case "0":
        default:
          return "response-bg-default";
      }
    },
    getResponseStatusTextClass(statusValue) {
      switch (statusValue) {
        case "1":
          return "response-text-success";
        case "2":
          return "response-text-error";
        case "3":
          return "response-text-warning";
        case "4":
          return "response-text-info";
        case "0":
        default:
          return "response-text-default";
      }
    },
    getDateObject(dateInput) {
      if (!dateInput) {
        return null;
      }

      if (dateInput.timestampToDate instanceof Date) {
        return dateInput.timestampToDate;
      }

      if (dateInput.timestamp?.toDate) {
        return dateInput.timestamp.toDate();
      }

      if (dateInput.toDate) {
        return dateInput.toDate();
      }

      const parsed = new Date(dateInput);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    },
    getReadableTimeSlotDate(dateInput) {
      if (dateInput?.timestampToReadableDate) {
        return dateInput.timestampToReadableDate;
      }
      const date = this.getDateObject(dateInput);
      if (!date) {
        return "-";
      }
      const options = {
        weekday: "short",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      };
      return date.toLocaleDateString("de-DE", options);
    },
    hasPositiveResponse(response) {
      return (response.status || []).some((status) => {
        return status.status === "1" || status.status === "3";
      });
    },
    hasAnyResponse(response) {
      return (response.status || []).some((status) => {
        return status.status && status.status !== "0";
      });
    },
    applyResponseFilter(responses) {
      if (this.helpers.responseFilter === "onlyPositive") {
        return responses.filter((response) =>
          this.hasPositiveResponse(response),
        );
      }
      if (this.helpers.responseFilter === "onlyResponded") {
        return responses.filter((response) => this.hasAnyResponse(response));
      }
      return responses;
    },
    ensureEditableResponse(uid, defaults = {}) {
      const responseIndex = this.requestResponses.findIndex(
        (item) => item.meta?.uid === uid,
      );
      if (responseIndex >= 0) {
        return responseIndex;
      }

      this.requestResponses.push({
        organizationId: this.$route.params.organizationId,
        requestId: this.$route.params.itemId,
        status: [],
        comment: "",
        meta: {
          uid,
        },
        ...defaults,
      });
      return this.requestResponses.length - 1;
    },
    updateStatus(response, timeSlotId, newStatusId) {
      if (!this.canWrite) {
        return;
      }
      const uid = response.user.uid;
      const responseIndex = this.ensureEditableResponse(uid, {
        user: response.user,
        group: response.group,
      });

      const existing = this.requestResponses[responseIndex];
      const currentStatus = Array.isArray(existing.status)
        ? [...existing.status]
        : [];
      const existingItemIndex = currentStatus.findIndex(
        (item) => item.id === timeSlotId,
      );

      if (existingItemIndex >= 0) {
        currentStatus[existingItemIndex] = {
          ...currentStatus[existingItemIndex],
          status: newStatusId,
          meta: this.getStatusItemMeta(),
        };
      } else {
        currentStatus.push({
          id: timeSlotId,
          status: newStatusId,
          isAccepted: false,
          comment: "",
          meta: this.getStatusItemMeta(),
        });
      }

      this.$set(this.requestResponses[responseIndex], "status", currentStatus);
      this.saveResponse(uid);
    },
    toggleAccepted(response, timeSlotId) {
      if (!this.canWrite) {
        return;
      }
      const uid = response.user.uid;
      const responseIndex = this.ensureEditableResponse(uid, {
        user: response.user,
        group: response.group,
      });

      const existing = this.requestResponses[responseIndex];
      const currentStatus = Array.isArray(existing.status)
        ? [...existing.status]
        : [];
      const existingItemIndex = currentStatus.findIndex(
        (item) => item.id === timeSlotId,
      );

      const existingItem =
        existingItemIndex >= 0 ? currentStatus[existingItemIndex] : null;
      const nextAccepted = existingItem ? !existingItem.isAccepted : true;
      const currentStatusValue = existingItem?.status || "0";
      const shouldSetPositiveStatus =
        nextAccepted && !["1", "3"].includes(currentStatusValue);
      const shouldUpdateMeta = shouldSetPositiveStatus || !existingItem;

      if (existingItemIndex >= 0) {
        currentStatus[existingItemIndex] = {
          ...currentStatus[existingItemIndex],
          isAccepted: nextAccepted,
          status: shouldSetPositiveStatus
            ? "1"
            : currentStatus[existingItemIndex].status,
          meta: shouldUpdateMeta
            ? this.getStatusItemMeta()
            : currentStatus[existingItemIndex].meta,
        };
      } else {
        currentStatus.push({
          id: timeSlotId,
          status: "1",
          isAccepted: true,
          comment: "",
          meta: this.getStatusItemMeta(),
        });
      }

      this.$set(this.requestResponses[responseIndex], "status", currentStatus);
      this.saveResponse(uid);
    },
    updateComment(response, value) {
      if (!this.canWrite) {
        return;
      }
      const uid = response.user.uid;
      const responseIndex = this.ensureEditableResponse(uid, {
        user: response.user,
        group: response.group,
      });
      this.$set(this.requestResponses[responseIndex], "comment", value || "");

      if (this.commentSaveTimeoutByUid[uid]) {
        clearTimeout(this.commentSaveTimeoutByUid[uid]);
      }
      this.commentSaveTimeoutByUid[uid] = setTimeout(() => {
        this.saveResponse(uid);
      }, 650);
    },
    updateTimeSlotComment(response, timeSlotId, value) {
      if (!this.canWrite) {
        return;
      }
      const uid = response.user.uid;
      const responseIndex = this.ensureEditableResponse(uid, {
        user: response.user,
        group: response.group,
      });

      const existing = this.requestResponses[responseIndex];
      const currentStatus = Array.isArray(existing.status)
        ? [...existing.status]
        : [];
      const existingItemIndex = currentStatus.findIndex(
        (item) => item.id === timeSlotId,
      );

      if (existingItemIndex >= 0) {
        currentStatus[existingItemIndex] = {
          ...currentStatus[existingItemIndex],
          comment: value || "",
          meta: this.getStatusItemMeta(),
        };
      } else {
        currentStatus.push({
          id: timeSlotId,
          status: "0",
          isAccepted: false,
          comment: value || "",
          meta: this.getStatusItemMeta(),
        });
      }

      this.$set(this.requestResponses[responseIndex], "status", currentStatus);

      const debounceKey = `${uid}_${timeSlotId}`;
      if (this.commentSaveTimeoutByUid[debounceKey]) {
        clearTimeout(this.commentSaveTimeoutByUid[debounceKey]);
      }
      this.commentSaveTimeoutByUid[debounceKey] = setTimeout(() => {
        this.saveResponse(uid);
      }, 650);
    },
    async saveResponse(uid) {
      const targetResponse = this.requestResponses.find(
        (response) => response.meta?.uid === uid,
      );
      if (!targetResponse) {
        return;
      }

      this.$set(this.saveStateByUid, uid, {
        saving: true,
        error: "",
      });

      const payload = {
        organizationId: this.$route.params.organizationId,
        itemId: this.$route.params.itemId,
        uid,
        comment: targetResponse.comment || "",
        status: (targetResponse.status || []).map((statusItem) => ({
          id: statusItem.id,
          status: statusItem.status || "0",
          isAccepted: !!statusItem.isAccepted,
          comment: statusItem.comment || "",
          meta: statusItem.meta || null,
        })),
      };

      try {
        await this.$store.dispatch(
          `${AVAILABILITY}${UPDATE_REQUEST_RESPONSE}`,
          payload,
        );
        this.$set(this.saveStateByUid, uid, {
          saving: false,
          error: "",
        });
      } catch (error) {
        this.$set(this.saveStateByUid, uid, {
          saving: false,
          error: "Speichern fehlgeschlagen",
        });
      }
    },
  },
};
</script>

<style scoped>
.matrix-search {
  max-width: 280px;
}

.matrix-assignment-menu-select {
  width: 100%;
}

.responses-theme-light {
  --member-col-width: 220px;
  --timeslot-col-width: 210px;
  --matrix-surface: #ffffff;
  --matrix-header-surface: #fafafa;
  --matrix-group-surface: #f3f4f6;
  --matrix-border: rgba(0, 0, 0, 0.08);
}

.responses-theme-dark {
  --member-col-width: 220px;
  --timeslot-col-width: 210px;
  --matrix-surface: #1e1e1e;
  --matrix-header-surface: #252526;
  --matrix-group-surface: #2e3136;
  --matrix-border: rgba(255, 255, 255, 0.12);
}

.responses-theme-light.responses-mode-manage,
.responses-theme-dark.responses-mode-manage {
  --timeslot-col-width: 285px;
}

.responses-matrix-wrapper {
  position: relative;
  overflow: auto;
  max-height: min(72vh, 900px);
}

.responses-matrix {
  min-width: 100%;
  width: max-content;
  position: relative;
}

.responses-matrix-row {
  display: grid;
  grid-template-columns: var(--member-col-width) repeat(
      var(--timeslot-columns),
      var(--timeslot-col-width)
    );
  border-bottom: 1px solid var(--matrix-border);
}

.responses-matrix-group-row {
  display: grid;
  grid-template-columns: var(--member-col-width) repeat(
      var(--timeslot-columns),
      var(--timeslot-col-width)
    );
  border-bottom: 1px solid var(--matrix-border);
  border-top: 1px solid var(--matrix-border);
  background: var(--matrix-group-surface);
}

.responses-matrix-group-cell {
  padding: 10px 12px;
  font-weight: 600;
}

.responses-matrix-group-divider-cell {
  grid-column: 2 / -1;
  border-left: 1px solid var(--matrix-border);
  background: var(--matrix-group-surface);
}

.responses-matrix-header {
  position: sticky;
  top: 0;
  z-index: 4;
}

.responses-matrix-cell {
  padding: 12px;
  border-right: 1px solid var(--matrix-border);
  background-clip: padding-box;
  box-sizing: border-box;
  min-width: 0;
}

.responses-matrix-timeslot-cell .caption,
.responses-matrix-timeslot-cell span {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.manage-timeslot-controls {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: 8px;
  align-items: start;
}

.manage-timeslot-select {
  min-width: 0;
}

.manage-timeslot-selection {
  width: 100%;
  min-width: 0;
}

.manage-timeslot-selection-label {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.1;
}

.manage-accept-btn {
  margin-top: 4px;
}

.responses-matrix-member-cell {
  position: sticky;
  left: 0;
  z-index: 2;
  background: var(--matrix-surface);
  width: var(--member-col-width);
  min-width: var(--member-col-width);
  max-width: var(--member-col-width);
}

.responses-matrix-member-header-cell {
  z-index: 3;
  background: var(--matrix-header-surface);
}

.responses-matrix-timeslot-header-cell {
  z-index: 2;
  background: var(--matrix-header-surface);
}

.responses-matrix-group-label-cell {
  position: sticky;
  left: 0;
  z-index: 2;
  background: var(--matrix-group-surface);
  width: var(--member-col-width);
  min-width: var(--member-col-width);
  max-width: var(--member-col-width);
}

.response-bg-default {
  background: #b0bec5;
}

.response-bg-success {
  background: #c8e6c9;
}

.response-bg-error {
  background: #ffcdd2;
}

.response-bg-warning {
  background: #ffe0b2;
}

.response-bg-info {
  background: #bbdefb;
}

.response-text-default {
  color: #37474f;
}

.response-text-success {
  color: #2e7d32;
}

.response-text-error {
  color: #c62828;
}

.response-text-warning {
  color: #ef6c00;
}

.response-text-info {
  color: #1565c0;
}

.responses-theme-dark .response-bg-default {
  background: #607d8b;
}

.responses-theme-dark .response-bg-success {
  background: #2e7d32;
}

.responses-theme-dark .response-bg-error {
  background: #c62828;
}

.responses-theme-dark .response-bg-warning {
  background: #ef6c00;
}

.responses-theme-dark .response-bg-info {
  background: #1565c0;
}

.responses-theme-dark .response-text-default,
.responses-theme-dark .response-text-success,
.responses-theme-dark .response-text-error,
.responses-theme-dark .response-text-warning,
.responses-theme-dark .response-text-info {
  color: #ffffff;
}

@media (max-width: 1264px) {
  .responses-theme-light,
  .responses-theme-dark {
    --member-col-width: 280px;
    --timeslot-col-width: 190px;
  }

  .responses-theme-light.responses-mode-manage,
  .responses-theme-dark.responses-mode-manage {
    --timeslot-col-width: 210px;
  }

  .responses-matrix-row {
    grid-template-columns: var(--member-col-width) repeat(
        var(--timeslot-columns),
        var(--timeslot-col-width)
      );
  }

  .responses-matrix-group-row {
    grid-template-columns: var(--member-col-width) repeat(
        var(--timeslot-columns),
        var(--timeslot-col-width)
      );
  }
}
</style>
